import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ExtracurricularSelectionPeriodEditModal from './ExtracurricularSelectionPeriodEditModal';

/**
 * Список «опросных периодов» — каждая строка = период, в который родители
 * выбирают, на какие внеурочные занятия будет ходить их ребёнок. Клик по
 * «Результаты» открывает матричную страницу с галочками и Excel-экспортом.
 */
const URL_SCHEMA = { create: '', edit: '' };

function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
// Превью HTML без тегов и без &nbsp; — для короткой колонки в таблице.
function htmlToPlain(s) {
  if (!s) return '';
  return String(s)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

export default function ExtracurricularSelectionsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite = can('extracurricular.students', 'write');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoad(true); setError(null);
    api.get('/api/extracurricular-selection-periods')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  };
  useEffect(reload, []);

  const editing = (() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  })();

  return (
    <>
      <Topbar
        breadcrumb={['Внеурочные занятия', 'Выбор занятий']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Опросы выбора занятий {!loading && <span className="muted">· {rows.length}</span>}
          </span>
          <span style={{ marginLeft: 14, fontSize: 12, color: 'var(--fg-muted)', maxWidth: 600 }}>
            Каждый период — это форма-опрос для родителей: они в личном кабинете отмечают,
            какие занятия посетит ребёнок. Здесь можно создать опрос и просмотреть/выгрузить ответы.
          </span>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет прав на запись'}
          >
            <Icon name="plus" size={13} /> Новый период
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">Периодов ещё нет. Создайте первый.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Даты</th>
                    <th style={{ width: 130 }}>Сбор заявок с</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Мин/Макс</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Заявок</th>
                    <th style={{ width: 200 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id} style={p.archive ? { opacity: 0.55 } : undefined}>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {fmtDateRu(p.dateStartPeriod)} – {fmtDateRu(p.dateEnd)}
                        </div>
                        {p.comment && (() => {
                          const plain = htmlToPlain(p.comment);
                          return (
                            <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                              {plain.length > 140 ? plain.slice(0, 140) + '…' : plain}
                            </div>
                          );
                        })()}
                        {p.archive && (
                          <span className="pay-st pay-st--mute" style={{ marginLeft: 6, fontSize: 10.5 }}>
                            в архиве
                          </span>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {fmtDateRu(p.dateStartSelection)}
                      </td>
                      <td className="mono" style={{ textAlign: 'right' }}>
                        {p.selectionMin || '—'} / {p.selectionMax || '—'}
                      </td>
                      <td className="mono" style={{ textAlign: 'right' }}>{p.resultsCount}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Link to={`/extracurricular/selections/${p.id}`}
                                className="btn btn--ghost btn--sm">
                            Результаты
                          </Link>
                          {canWrite && (
                            <button className="btn btn--ghost btn--sm"
                                    onClick={() => setParams({ edit: String(p.id) })}
                                    title="Редактировать">
                              <Icon name="edit" size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ExtracurricularSelectionPeriodEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          period={editing === 'new' ? null : editing}
          onClose={() => setParams({ edit: '' })}
          onSaved={() => { setParams({ edit: '' }); reload(); }}
          onDeleted={() => { setParams({ edit: '' }); reload(); }}
        />
      )}
    </>
  );
}
